import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Eye, FileDown, CheckCircle, IndianRupee, Coins } from "lucide-react";
import { formatCurrency } from "../../lib/currency";

const PayrollTable = ({
  payrolls,
  loading,
  getEmployeeName,
  onViewDetail,
  onDownloadPayslip,
  onProcess,
  currency,
}) => {
  const fmt = (amount) => formatCurrency(amount, currency);

  return (
    <Card className="card-soft" data-testid="payroll-table">
      <CardHeader>
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <IndianRupee className="h-5 w-5" />
          Payroll Records
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
          </div>
        ) : payrolls.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-500">
            <Coins className="h-12 w-12 mb-3 text-slate-300" />
            <p>No payroll records yet. Generate payroll for your employees.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wide text-slate-400">Employee</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-400">Period</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-400">Gross</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-400">PF</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-400">ESI</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-400">PT</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-400">TDS</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-400">Adv/Loan</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-400">LOP</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-400">Net Salary</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-400">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payrolls.map((payroll) => (
                  <TableRow key={payroll.id} className="hover:bg-slate-50/50" data-testid={`payroll-row-${payroll.id}`}>
                    <TableCell className="font-medium text-slate-800">
                      {getEmployeeName(payroll.employee_id)}
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {payroll.month} {payroll.year}
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {fmt(payroll.gross_salary || (payroll.basic_salary + (payroll.allowances || 0)))}
                    </TableCell>
                    <TableCell className="text-red-600">
                      {fmt(payroll.pf_employee || 0)}
                    </TableCell>
                    <TableCell className="text-red-600">
                      {fmt(payroll.esi_employee || 0)}
                    </TableCell>
                    <TableCell className={payroll.professional_tax > 0 ? "text-red-600" : "text-slate-400"}>
                      {payroll.professional_tax > 0 ? fmt(payroll.professional_tax) : "—"}
                    </TableCell>
                    <TableCell className="text-red-600">
                      {fmt(payroll.tds || payroll.tax || 0)}
                    </TableCell>
                    <TableCell className={payroll.advance_deduction > 0 ? "text-orange-600 font-medium" : "text-slate-400"}>
                      {payroll.advance_deduction > 0 ? fmt(payroll.advance_deduction) : "—"}
                    </TableCell>
                    <TableCell className={payroll.lop_amount > 0 ? "text-blue-600 font-medium" : "text-slate-400"}>
                      {payroll.lop_amount > 0 ? fmt(payroll.lop_amount) : "—"}
                    </TableCell>
                    <TableCell className="font-semibold text-slate-800">
                      {fmt(payroll.net_salary)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          payroll.status === "pending"
                            ? "bg-amber-50 text-amber-700 border-amber-200"
                            : "bg-green-50 text-green-700 border-green-200"
                        }
                      >
                        {payroll.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onViewDetail(payroll)}
                          className="rounded-full"
                          data-testid={`view-payroll-${payroll.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDownloadPayslip(payroll.id)}
                          className="rounded-full text-blue-600"
                          data-testid={`download-payslip-${payroll.id}`}
                        >
                          <FileDown className="h-4 w-4" />
                        </Button>
                        {payroll.status === "pending" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onProcess(payroll.id)}
                            className="rounded-full text-green-600"
                            data-testid={`process-payroll-${payroll.id}`}
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PayrollTable;
